/* eslint-disable */
import { defineStore } from 'pinia';
import MutationCall from '~/graphql/wrappers/mutation-call';
import QueryCall from '~/graphql/wrappers/query-call';
import { deleteTokens, storeTokens } from '~/plugins/urql';

import {
  type AuthResult,
  ChangePasswordDocument,
  type ChangePasswordMutation,
  type ChangePasswordMutationVariables,
  EmailSignupDocument,
  type EmailSignupMutation,
  type EmailSignupMutationVariables,
  Language,
  LoginDocument,
  type LoginMutation,
  type LoginMutationVariables,
  MailVerificationStatus,
  MeDocument,
  type MeQuery,
  RequestUserPasswordResetDocument,
  type RequestUserPasswordResetMutation,
  type RequestUserPasswordResetMutationVariables,
  RequestVerificationEmailDocument,
  type RequestVerificationEmailMutation,
  type RequestVerificationEmailMutationVariables,
  UpdateUserDocument,
  UpdateUserEmailDocument,
  type UpdateUserEmailMutation,
  type UpdateUserEmailMutationVariables,
  type UpdateUserInput,
  type UpdateUserMutation,
  type UpdateUserMutationVariables,
  type User,
  VerifyUserEmailDocument,
  type VerifyUserEmailMutation,
  type VerifyUserEmailMutationVariables,
  VerifyUserPasswordResetDocument,
  type VerifyUserPasswordResetMutation,
  type VerifyUserPasswordResetMutationVariables,
} from '~/graphql';

import useTenantInvite from '~/composables/tenant/useTenantInvite';
import { redirectUserIfNotAllowedCurrentPath } from '~/middleware/auth.global';
import { useCourseCollaborationManagement } from '~/stores/courseCollaborationManagement';
import {
  AvailableClassregisterIntegrations,
  AvailableTimetableIntegrations,
} from '~/utils/constants';
import {
  ExportLibraryDocument,
  type ExportLibraryMutation,
  type ExportLibraryMutationVariables,
} from '../graphql';

let loginMutationCall: MutationCall<LoginMutation, LoginMutationVariables>;
let emailSignUpMutationCall: MutationCall<
  EmailSignupMutation,
  EmailSignupMutationVariables
>;
let requestVerificationEmailMutationCall: MutationCall<
  RequestVerificationEmailMutation,
  RequestVerificationEmailMutationVariables
>;
let verifyUserEmailMutationCall: MutationCall<
  VerifyUserEmailMutation,
  VerifyUserEmailMutationVariables
>;

let updateUserMutationCall: MutationCall<
  UpdateUserMutation,
  UpdateUserMutationVariables
>;

let updateUserEmailMutationCall: MutationCall<
  UpdateUserEmailMutation,
  UpdateUserEmailMutationVariables
>;
let requestUserPasswordResetCall: MutationCall<
  RequestUserPasswordResetMutation,
  RequestUserPasswordResetMutationVariables
>;
let verifyUserPasswordResetCall: MutationCall<
  VerifyUserPasswordResetMutation,
  VerifyUserPasswordResetMutationVariables
>;
let changePasswordCall: MutationCall<
  ChangePasswordMutation,
  ChangePasswordMutationVariables
>;

let exportLibraryContentCall: MutationCall<
  ExportLibraryMutation,
  ExportLibraryMutationVariables
>;
let meQueryCall: QueryCall<MeQuery>;

export class LoginError extends Error {}

export class UserConflictError extends Error {}

export class InvalidCodeError extends Error {}

export class LimitReachedError extends Error {}

export class ItemExpiredError extends Error {}

let courseCollaborationManagementStore = null;

let acceptTenantCodeAfterLogin = null;

type UserItem = {
  firstName: string;
  lastName: string;
}

export const useSession = defineStore('session', {
  state: () => ({
    loggedIn: localStorage.getItem('loggedIn') ?? false,
    user: localStorage.getItem('user')
      ? JSON.parse(localStorage.getItem('user')!)
      : null,
    activeIntegrations: [],
    licenses: [],
    prefillUsername: '',
    userLoaded: false,
  }),
  getters: {
    isLoggedIn: (state) => state.loggedIn,
    isSignedUp: (state) => !!state.user.signedUp,
    needsEmailVerification: (state) =>
      state.user.emailVerificationStatus == MailVerificationStatus.Unverified,
    getUser: (state): UserItem => state.user,
    getUserId: (state) => state.user?.id,
    getFullName: (state) => `${state.user?.lastName} ${state.user?.firstName}`,
    getEmail: (state): string => state.user?.email,
    getInitials: (state) =>
      state.user
        ? `${state.user?.firstName.charAt(0).toUpperCase()}${state.user?.lastName
            .charAt(0)
            .toUpperCase()}`
        : '',
    getDefaultTenantId: (state) => state.user?.defaultTenant.id,
    isTeacher: (state) => state.user?.defaultTenant.permissions.isTenantTeacher,
    canCreateCourse: (state) =>
      state.user?.defaultTenant.permissions.canCreateCourse,
    getUserLanguage: (state): Language => state.user?.language ?? Language.De,
    getPrefillUsername: (state) => state.prefillUsername,
    hasActiveClassRegisterIntegration: (state) =>
      state.activeIntegrations.find(
        (integration) =>
          AvailableClassregisterIntegrations.indexOf(
            integration.externalIntegrationSetting.key,
          ) > -1,
      ) !== undefined,
    hasActiveTimetableIntegration: (state) =>
      state.activeIntegrations.find(
        (integration) =>
          AvailableTimetableIntegrations.indexOf(
            integration.externalIntegrationSetting.key,
          ) > -1,
      ) !== undefined,
    hasActiveEuropathekIntegration: (state) =>
      state.activeIntegrations.find(
        (integration) => integration.externalIntegrationSetting.key === 'europathek'
      ) !== undefined,
    getLicenses: (state) => state.licenses,
  },
  actions: {
    init() {
      loginMutationCall = new MutationCall(LoginDocument);
      emailSignUpMutationCall = new MutationCall(EmailSignupDocument);
      requestVerificationEmailMutationCall = new MutationCall(
        RequestVerificationEmailDocument,
      );
      verifyUserEmailMutationCall = new MutationCall(VerifyUserEmailDocument);
      updateUserMutationCall = new MutationCall(UpdateUserDocument);
      updateUserEmailMutationCall = new MutationCall(UpdateUserEmailDocument);
      requestUserPasswordResetCall = new MutationCall(
        RequestUserPasswordResetDocument,
      );
      verifyUserPasswordResetCall = new MutationCall(
        VerifyUserPasswordResetDocument,
      );
      changePasswordCall = new MutationCall(ChangePasswordDocument);
      meQueryCall = new QueryCall<MeQuery>(MeDocument);
      courseCollaborationManagementStore = useCourseCollaborationManagement();
      courseCollaborationManagementStore.init();
      acceptTenantCodeAfterLogin = useTenantInvite().acceptCodeAfterLogin;
      exportLibraryContentCall = new MutationCall(ExportLibraryDocument);
    },
    setPrefillUsername(username: string) {
      this.prefillUsername = username;
    },
    logOut() {
      deleteTokens();
      localStorage.removeItem('user');
      localStorage.removeItem('loggedIn');
      localStorage.clear();
      this.loggedIn = false;
      this.user = null;
      this.prefillUsername = '';
      this.activeIntegrations = [];
      this.licenses = [];
      this.userLoaded = false;

      const { getMeRef } = useCurrentUser();
      getMeRef().value = null;
    },
    logOutAndRedirect() {
      this.logOut();
      window.location.href = '/auth/login';
    },
    async logIn(credentials: LoginMutationVariables) {
      const result = await loginMutationCall.run(credentials);
      if (result.hasError()) {
        if (
          result.getError()?.getCode() == 'INVALIDCREDENTIALS' ||
          result.getError()?.getCode() == 'PasswordInput'
        ) {
          throw new LoginError();
        } else {
          throw result.getError();
        }
      }
      await this.initialiseLoggedInUser(result.getData().login);
    },
    async initialiseLoggedInUser(authResult: AuthResult) {
      storeTokens(authResult);
      this.loggedIn = true;
      await this.loadUser();
    },
    async loadUser() {
      const me = await meQueryCall.run();
      if (me.hasError()) {
        throw me.getError();
      }

      const data = me.getData();
      const user = data.me as User;
      const activeIntegrations = data.externalIntegrations;
      const licences = data.licenses;
      this.storeUser(user, activeIntegrations, licences);

      const { getMeRef } = useCurrentUser();
      getMeRef().value = data;


      localStorage.setItem(
        'loggedIn',
        JSON.stringify(this.isLoggedIn ? true : false),
      );

      // accept the course invite if it was set before the login action
      await courseCollaborationManagementStore.acceptInviteAfterLogin();

      // accept the tenant invite if it was set before the login action
      await acceptTenantCodeAfterLogin();
    },
    async loadUserIfNeeded() {
      if (!this.userLoaded && this.isLoggedIn) {
        await this.loadUser();
      }
    },
    async reloadUserOrLogOut() {
      if (this.isLoggedIn) {
        try {
          await this.loadUser();
          redirectUserIfNotAllowedCurrentPath();
        } catch (e) {
          console.log(e);
        }
      }
    },
    storeUser(user: User, activeIntegrations: [], licenses: []) {
      this.user = user;
      this.activeIntegrations = activeIntegrations;
      this.licenses = licenses;
      this.prefillUsername = user.email;
      this.userLoaded = true;
      localStorage.setItem('user', JSON.stringify(user));
    },
    async signUpEmail(
      email: string,
      dataPolicyAccepted: boolean,
      newsletterAccepted = false,
    ) {
      const mutationVariables: EmailSignupMutationVariables = {
        emailSignUpInput: {
          newsletterAccepted,
          dataPolicyAccepted,
          email,
        },
      };
      const result = await emailSignUpMutationCall.run(mutationVariables);
      if (result.hasData()) {
        await this.initialiseLoggedInUser(result.getData().emailSignup);
      } else {
        if (
          result.getError() &&
          result.getError()?.getCode() == 'UserConflict'
        ) {
          throw new UserConflictError();
        } else {
          throw new Error();
        }
      }
    },

    async signUpEmailAndMasterdata(
      email: string,
      firstName: string,
      lastName: string,
      password: string,
      dataPolicyAccepted: boolean,
      newsletterAccepted = false,
      signupMeta: {
        signupMedium?: string;
        signupSource?: string;
        signupCampaign?: string;
        signupContent?: string;
      }
    ) {
      const mutationVariables: EmailSignupMutationVariables = {
        emailSignUpInput: {
          newsletterAccepted,
          dataPolicyAccepted,
          email,
          firstName,
          lastName,
          password,
          ...signupMeta,
        },
      };
      const result = await emailSignUpMutationCall.run(mutationVariables);
      if (result.hasData()) {
        await this.initialiseLoggedInUser(result.getData().emailSignup);
      } else {
        if (
          result.getError() &&
          result.getError()?.getCode() == 'UserConflict'
        ) {
          throw new UserConflictError();
        } else {
          throw new Error();
        }
      }
    },
    async resendVerificationEmail() {
      const result = await requestVerificationEmailMutationCall.run({});
      if (result.hasError()) {
        const error = result.getError();
        if (error?.getCode() == 'LimitReached') {
          throw new LimitReachedError();
        } else {
          throw new Error();
        }
      }
    },
    async updateUserLanguage(language: Language) {
      const result = await updateUserMutationCall.run({
        updateUserInput: {
          language,
        },
      });

      if (result.hasError()) {
        throw result.getError();
      }
      this.updateLanguage(result.getData().updateUser.user.language);
    },
    async updateUser(updateUserInput: UpdateUserInput) {
      const result = await updateUserMutationCall.run({
        updateUserInput,
      });

      if (result.hasError()) {
        throw result.getError();
      }
      this.updateLanguage(result.getData().updateUser.user.language);
    },
    updateLanguage(language: Language) {
      const updatedUser = {
        ...this.user,
        language,
      };

      this.user = updatedUser;
      localStorage.setItem('user', JSON.stringify(updatedUser));
    },
    async changePassword(existingPassword: string, newPassword: string) {
      const result = await changePasswordCall.run({
        changePasswordInput: { existingPassword, newPassword },
      });

      if (result.getError()) {
        if (result.getError()!.getCode() == 'UNAUTHENTICATED') {
          throw new LoginError();
        } else {
          throw result.getError();
        }
      }
    },

    async updateEmail(email: string) {
      const result = await updateUserEmailMutationCall.run({
        emailInput: {
          email,
        },
      });
      if (result.hasError()) {
        const error = result.getError();
        if (error?.getCode() == 'NotAllowed') {
          throw new UserConflictError();
        } else if (error?.getCode() == 'EmailVerificationLimit') {
          throw new LimitReachedError();
        } else {
          throw new Error();
        }
      }
    },
    async verifyEmail(code: string) {
      const result = await verifyUserEmailMutationCall.run({
        verificationInput: { code },
      });
      if (result.hasError()) {
        const error = result.getError();
        if (error?.getCode() == 'NotFound') {
          throw new InvalidCodeError();
        } else if (error?.getCode() == 'LimitReached') {
          throw new LimitReachedError();
        } else if (error?.getCode() == 'ItemExpired') {
          throw new ItemExpiredError();
        } else {
          throw new Error();
        }
      }
      await this.loadUser();
    },
    async requestResetPassword(email: string) {
      const result = await requestUserPasswordResetCall.run({
        emailInput: { email },
      });
      if (result.hasError()) {
        const error = result.getError();
        if (error?.getCode() == 'LimitReached') {
          throw new LimitReachedError();
        } else {
          throw new Error();
        }
      }
    },
    async exportLibraryContent() {
      const result = await exportLibraryContentCall.run();
      if (result.hasError()) {
        const error = result.getError();
        console.log(error);
        throw new Error();
      }
    },
  },
});
